import React, {useEffect, useState} from "react";
import { RdmpCondService } from "app/services/rp/RdmpCondService";
import { ResponseResultCode } from "app/model/response-api";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import {parseNumberWithCommaDecimal, round, roundWithComma} from "app/utils/stringUtil";
import DashboardLoading from "./DashboardLoading";
import DashboardNoData from "./DashboardNoData";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";

const options: Highcharts.Options = {
  chart: {
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
  },
  colors: ['#545CC1', '#4C96F9', '#44C5DB', '#9F92FF', '#FFBB24'],
  legend: {
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'middle',
    borderWidth: 0,
    symbolWidth: 0,
    symbolHeight: 0,
    symbolPadding: 0,
    itemMarginTop: 15,
    itemMarginBottom: 15,
    width: 350,
    useHTML: true,
    labelFormatter: function(this: any) {
      return `
        <div style="width: 15rem; display: flex; justify-content: space-between; align-items: center;">
          <span style="font-size: 0.75rem; color: #555;"><span style="color: ${this.color};">●&nbsp;</span>${this.name}</span>
          <span style="font-size: 0.75rem; color: #666;"><span style="font-size: 0.75rem; color: #333; font-weight: 500;">${parseNumberWithCommaDecimal(this.y)}</span> kWh</span>
        </div>
      `;
    },
  },
  tooltip: {
    formatter: function (this: any) {
      return `
        <div>
          <span style="color: ${this.color};">●&nbsp;</span>
          <span style="font-weight:500;">${this.point.name}:&nbsp;</span>
          <span style="font-weight:700;">${roundWithComma(this.point.y, 2)}</strong></span> kWh (${round(this.point.percentage, 2)}%)
        </div>
      `;
    },
    style: {
      fontSize: '0.75rem'
    }
  },
  series: [
    {
      name: '데이터 없음',
      type: 'pie',
      data: [{ name: '데이터 없음', color: '#E0E0E0' }]
    }
  ],
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: 'pointer',
      showInLegend: true,
      borderWidth: 0,
      borderRadius: 0,
      innerSize: '60%',
      dataLabels: {
        useHTML: true,
        enabled: true,
        distance: -15,
        formatter(this: any) {
          return (
            `<div style="text-align:center;">
              <span class="bar-graph-color-num" style="position:relative;display:inline-block;margin-top:5px;padding:2px 5px;font-weight:400;border-radius:15px;overflow:hidden;background: ${this.color}">
                <span style="position:relative;display:block;z-index:1;color:#fff;font-size:.7rem;font-weight:600;">
                  ${round(this.percentage, 2)}%
                </span>
                <em style="position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.3);"></em>                        
              </span>
            </div>`
          );
        },
      },
    },
  },
  responsive: {
    rules: [{
      condition: {
        maxWidth: 500,
      },
      chartOptions: {
        legend: {
          layout: 'vertical',
          align: 'center',
          verticalAlign: 'bottom',
          width: 300,
        },
      }
    }]
  }
};

export default function FuflMeansRt(props: Readonly<DashboardModuleProps>) {
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    colors: ['#4C96F9', '#00BB9F', '#F36363'],
    title: { text: "" },
    exporting: {enabled: false},
    credits: {enabled: false},
    tooltip: {shared: true, headerFormat: ''},
    accessibility: {enabled: false},
  });

  const [chartOption, setChartOption] = useState<any>(options);
  const [isLoading, setIsLoading] = useState(true);
  const [existsLastScnro, setExistsLastScnro] = useState<boolean>(false);

  useEffect(() => {
    getLatestScnro().finally(() => {
      setIsLoading(false);
    });
  }, []);

  async function getLatestScnro() {
    const response = await RdmpCondService.getInstance().getRegistScnro();
    if (response && ResponseResultCode.Y === response.resultCode) {
      setExistsLastScnro(response.data?.scnroId != null);
      const chartData = response.data.scnroInfoList?.map((item: any) => {
        let name;
        switch (item.emksFuflMeansCd) {
          case 1:
            name = '자가발전';
            break;
          case 2:
            name = '인증서(REC)구매';
            break;
          case 3:
            name = '녹색프리미엄';
            break;
          case 6:
            name = 'PPA';
            break;
          default:
            name = '기타';
        }
        return {
          name: name,
          y: item.fuflMeansGoalQnt
        };
      });

      const noData:boolean = chartData ? chartData.every(item => item.y === 0) : true;

      setChartOption((prevState: any) => {
        return {
          ...prevState,
          series: [{
            name: '이행수단 비율',
            type: 'pie',
            data: chartData,
            borderWidth: noData ? 0.5 : 0
          }]
        };
      });
    } else {
      setChartOption((prevState: any) => {
        return {
          ...prevState,
          series: [{
            name: '이행수단 비율',
            type: 'pie',
            data: {
              name: '',
              y: 0
            },
            borderWidth:0.5,
          }]
        };
      });
    }
  }


  return (
    <DashboardCard
      title="이행수단 비율"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={false}
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {!isLoading && !existsLastScnro ? (
        <DashboardNoData
          message="등록된 RE100 포트폴리오가 없습니다."
          link={{text: '포트폴리오 등록하기', to: '/rp/portfolio'}}
          className="ex-data3"
        />
      ) : (
        <div className="chart">
          <HighchartsReact highcharts={Highcharts} options={chartOption} />
        </div>
      )}
    </DashboardCard>
  );
}